/**
 * MsgType 枚举
 * 用于区分 MsgBean.sub_type 字段的具体消息类型
 * 来源：后端 Kotlin 定义，对应消息表的 sub_type 字段
 * 文档：docs/IM架构/逻辑.md (行 502-581)
 */

// ========== 私聊消息类型 ==========
export const PRIVATE_MSG_TYPES = {
  TEXT: 'TextDef', // 文本
  IMAGE: 'ImageDef', // 图片
  IMAGE_PRIVATE: 'ImagePrivateDef', // 私密图片
  IMAGE_NEED_AUDIT: 'ImageNeedAuditDef', // 风控
  IMAGE_EMOJI: 'ImageEmojiDef', // Emoji
  AUDIO: 'AudioDef', // 音频
  VIDEO: 'VideoDef', // 视频
  HINT: 'HintDef', // Hint提示消息
  HINT_VIDEO: 'HintVideo', // HintVideo消息
  HINT_DOUBLE: 'HintDoubleDef', // 双方都能看到的hint
  HINT_HYPERLINK: 'HintHyperlink', // 超链接提示消息
  HINT_INDUCE_VIDEO: 'HintInduceVideo', // 诱导视频提示消息
  HINT_APPOINTMENT_VIDEO: 'HintAppointmentCallback', // 预约回拨
  CONSUME_RECORD: 'ConsumeRecordDef', // 礼物消息
  BOTH_SIDE_TEXT: 'BothSideTextDef', // 双边文本消息
  CONTROL_COMMAND_UPDATE: 'ControlCommandUpdateAdditionalDef', // 控制消息修改content的additional字段
  QA_OPTION: 'QAOptionDef', // 问答选项消息
  QA_BUTTON: 'QAButtonDef', // 按钮类型消息
  CUSTOM_ACTION_TEXT: 'CustomActionTextDef', // 自定义跳转消息
  COMMON_ACTION_TEXT: 'CommonActionTextDef', // 文本超链接消息
  COMMON_MOMENT: 'CommonMomentDef', // 动态消息
  COMMON_LIVE_ROOM: 'CommonLiveRoomDef', // 直播间分享
  COMMON_GAME: 'CommonGameDef', // 游戏邀请
  COMMON_CALL: 'CommonCallDef', // 专属通话卡
  GIFT_REQUEST: 'GiftRequestDef', // 专属礼物消息
  INVITE_JOIN_UNION: 'InviteJoinUnionDef', // 邀请加入公会
  CUSTOM_ACTION_CHATROOM_INVITE: 'CustomActionChatRoomInviteDef', // 邀请加入家族
  IWEE_TEAM: 'IweeTeamDef' // Team团队消息
}

// ========== 家族（ChatRoom）消息类型 ==========
export const CHATROOM_MSG_TYPES = {
  TEXT: 'ChatRoomText', // 文字类型
  TEXT_DEF: 'ChatRoomTextDef', // 接收文字消息
  EMOJI: 'ChatRoomEmoji', // Emoji类型
  EMOJI_DEF: 'ChatRoomEmojiDef', // 接收Emoji类型
  IMAGE: 'ChatRoomImage', // 图片类型
  IMAGE_DEF: 'ChatRoomImageDef', // 接收图片类型
  GIFT: 'ChatRoomGiftDef', // 家族送礼
  HINT: 'ChatRoomHintDef', // 家族hint
  LIVE_ROOM_INVITE: 'ChatRoomLiveRoomInviteDef' // 分享直播间到聊天室
}

// ========== 所有消息类型（合并） ==========
export const MsgType = {
  ...PRIVATE_MSG_TYPES,
  ...CHATROOM_MSG_TYPES
}

// ========== 消息来源 ==========
export const MSG_SOURCE = {
  SYSTEM_DEFAULT_MSG_SOURCE: 'SystemDefaultMsgSource', // 系统默认消息源
  HINT_AND_HINT_DOUBLE: 'HintAndHintDouble', // Hint 和 HintDouble 消息使用此值
  SYSTEM_NOTIFICATION: 'SystemNotification', // 系统通知、定时任务消息等
  FROM_USER: 'FromUser', // 用户自发消息
  AI_AGENT: 'AIAgent', // AI 代理消息
  AI_AUTO_REPLY: 'AIAutoReply', // AI 自动回复的消息
  INSTEAD_MSG_SOURCE: 'InsteadMsgSource', // 代发消息源
  INSTEAD_TO_ANCHOR: 'InsteadToAnchor', // 代发给主播的消息
  LADO_SAY_HI_MSG_SOURCE: 'LadoSayHiMsgSource', // Lado 打招呼消息源
  LADO_SAY_HI_MSG: 'LadoSayHiMsg', // Lado 应用中的打招呼消息
  MSG_CONTACT_SOURCE: 'MsgContactSource', // 消息联系人源
  CUSTOMER_SERVICE_AUTO_REPLY: 'CustomerServiceAutoReply', // 客服自动回复的消息
  CONSUMER_MSG_SOURCE: 'ConsumerMsgSource', // 消费者消息源
}
// ========== 前端支持的消息类型 ==========

/**
 * 前端聊天页面支持显示的私聊消息类型
 * 用于：
 * 1. MessageItem 组件判断是否显示 UnsupportedMessage
 * 2. 消息预览生成时判断是否使用原始预览
 */
export const SUPPORTED_PRIVATE_MSG_TYPES = [
  PRIVATE_MSG_TYPES.TEXT,
  PRIVATE_MSG_TYPES.IMAGE,
  PRIVATE_MSG_TYPES.IMAGE_PRIVATE,
  PRIVATE_MSG_TYPES.IMAGE_NEED_AUDIT,
  // PRIVATE_MSG_TYPES.IMAGE_EMOJI,
  PRIVATE_MSG_TYPES.HINT_VIDEO,
  PRIVATE_MSG_TYPES.HINT,
  PRIVATE_MSG_TYPES.HINT_DOUBLE,
]

/**
 * 检查是否是文本类型消息
 * @param {string} msgType - 消息类型
 * @returns {boolean}
 */
export function isTextMessage(msgType) {
  return [
    MsgType.TEXT,
    MsgType.BOTH_SIDE_TEXT,
    MsgType.CUSTOM_ACTION_TEXT,
    MsgType.COMMON_ACTION_TEXT,
    MsgType.CHATROOM_TEXT,
    MsgType.CHATROOM_TEXT_DEF
  ].includes(msgType)
}

/**
 * 检查是否是图片类型消息
 * @param {string} msgType - 消息类型
 * @returns {boolean}
 */
export function isImageMessage(msgType) {
  return [
    MsgType.IMAGE,
    MsgType.IMAGE_PRIVATE,
    MsgType.IMAGE_NEED_AUDIT,
    MsgType.IMAGE_EMOJI,
    MsgType.CHATROOM_IMAGE,
    MsgType.CHATROOM_IMAGE_DEF
  ].includes(msgType)
}

/**
 * 检查是否是媒体类型消息（图片、视频、音频）
 * @param {string} msgType - 消息类型
 * @returns {boolean}
 */
export function isMediaMessage(msgType) {
  return [
    MsgType.IMAGE,
    MsgType.IMAGE_PRIVATE,
    MsgType.VIDEO,
    MsgType.AUDIO,
    MsgType.CHATROOM_IMAGE,
    MsgType.CHATROOM_IMAGE_DEF
  ].includes(msgType)
}

/**
 * 检查是否是 Hint 类型消息
 * @param {string} msgType - 消息类型
 * @returns {boolean}
 */
export function isHintMessage(msgType) {
  return [
    MsgType.HINT,
    MsgType.HINT_VIDEO,
    MsgType.HINT_DOUBLE,
    MsgType.HINT_HYPERLINK,
    MsgType.HINT_INDUCE_VIDEO,
    MsgType.HINT_APPOINTMENT_VIDEO,
    MsgType.CHATROOM_HINT
  ].includes(msgType)
}

/**
 * 检查是否是家族消息
 * @param {string} msgType - 消息类型
 * @returns {boolean}
 */
export function isChatRoomMessage(msgType) {
  return Object.values(CHATROOM_MSG_TYPES).includes(msgType)
}

/**
 * 获取消息类型的显示名称（用于UI展示）
 * @param {string} msgType - 消息类型
 * @returns {string}
 */
export function getMsgTypeDisplayName(msgType) {
  const displayNames = {
    [MsgType.TEXT]: 'Text',
    [MsgType.IMAGE]: 'Image',
    [MsgType.IMAGE_PRIVATE]: 'Private Image',
    [MsgType.IMAGE_EMOJI]: 'Emoji',
    [MsgType.VIDEO]: 'Video',
    [MsgType.AUDIO]: 'Voice',
    [MsgType.HINT]: 'Notification',
    [MsgType.CONSUME_RECORD]: 'Gift',
    [MsgType.COMMON_MOMENT]: 'Moment',
    [MsgType.COMMON_LIVE_ROOM]: 'Live Room',
    [MsgType.COMMON_GAME]: 'Game Invitation',
    [MsgType.COMMON_CALL]: 'Call Card',
    [MsgType.GIFT_REQUEST]: 'Gift Request',
    [MsgType.IWEE_TEAM]: 'Team Message',
    [MsgType.CHATROOM_TEXT]: 'Chat Room Text',
    [MsgType.CHATROOM_IMAGE]: 'Chat Room Image',
    [MsgType.CHATROOM_GIFT]: 'Chat Room Gift'
  }
  return displayNames[msgType] || 'Message'
}

export default MsgType
